import React from 'react'
import Calendar from '@/shared/components/Calendar'
import clsx from 'clsx'
import { endOfYear, format, startOfYear } from 'date-fns'
import { DayPickerProps, Matcher } from 'react-day-picker'
import { Popover } from '@headlessui/react'
import { SizeVariants } from '@/shared/forms/components/constants'
import { CalendarDaysIcon } from '@heroicons/react/20/solid'

type LegacyCaptionLayout =
  | DayPickerProps['captionLayout']
  | 'buttons'
  | 'dropdown-buttons'

export type DatePickerProps = Omit<
  DayPickerProps,
  'mode' | 'captionLayout' | 'selected' | 'onSelect'
> & {
  onTouch: () => void
  error?: string
  inputIsDisabled?: boolean
  selected?: Date
  onSelect?: (...args: Array<any>) => void
  fromDate?: Date
  toDate?: Date
  captionLayout?: LegacyCaptionLayout
  variant?: 'day-only' | 'month-and-year'
  buttonClassName?: string
  appearance?: 'default' | 'portal-profile'
}

export default function DatePicker({
  id,
  selected,
  error,
  onSelect,
  inputIsDisabled,
  onTouch,
  fromDate,
  toDate,
  captionLayout,
  variant = 'day-only',
  buttonClassName,
  appearance = 'default',
  ...props
}: DatePickerProps) {
  let startMonth: Date | undefined = props.startMonth
  let endMonth: Date | undefined = props.endMonth
  const disabledDates: Matcher[] = []

  // We still want to show full months from Jan to Dec because dumb users.
  // If we limit with start month, sometimes not all months will be pickable.
  if (fromDate && fromDate instanceof Date) {
    disabledDates.push({ before: fromDate })

    if (variant === 'month-and-year' && !startMonth) {
      startMonth = startOfYear(fromDate)
    }
  }
  if (toDate && toDate instanceof Date) {
    disabledDates.push({ after: toDate })

    if (variant === 'month-and-year' && !endMonth) {
      endMonth = endOfYear(toDate)
    }
  }

  return (
    <div className="mt-2">
      <Popover className="relative">
        <Popover.Button
          className={clsx(
            appearance === 'portal-profile'
              ? 'block h-[52px] w-full rounded-[14px] border-[1.5px] bg-[#F4F4F6] px-4 py-0 text-left text-[15px] leading-5 text-[#0B0B0F] outline-none transition-colors duration-300 focus:border-[#0B0B0F]/45'
              : 'block h-10 w-full rounded-md border-0 px-3 py-1 text-left text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
            !selected && 'text-muted-foreground',
            error
              ? appearance === 'portal-profile'
                ? 'border-[#C6453C]'
                : 'ring-red-600'
              : appearance === 'portal-profile'
                ? 'border-transparent'
                : 'ring-gray-300',
            inputIsDisabled
              ? appearance === 'portal-profile'
                ? 'cursor-not-allowed opacity-60'
                : 'bg-slate-300'
              : appearance === 'portal-profile'
                ? ''
                : 'bg-white',
            buttonClassName,
          )}
          id={id}
          disabled={inputIsDisabled}
          onClick={() => onTouch()}
        >
          <span
            className={clsx(
              appearance === 'portal-profile' &&
                'flex w-full items-center justify-between gap-3',
            )}
          >
            {selected ? format(selected, 'dd/MM/yyyy') : 'Pick a date'}
            {appearance === 'portal-profile' && (
              <CalendarDaysIcon
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-[#82828B]"
              />
            )}
          </span>
        </Popover.Button>
        <Popover.Panel
          portal
          anchor={{ to: 'bottom start', gap: '4px', padding: '8px' }}
          className="z-[110] w-auto rounded-lg bg-white p-0 shadow ring-1 ring-gray-200"
        >
          {({ close }) => (
            <Calendar
              {...props}
              mode="single"
              startMonth={startMonth}
              endMonth={endMonth}
              captionLayout="dropdown"
              navLayout="around"
              selected={selected}
              disabled={disabledDates}
              onSelect={(day, selectedDay, activeModifiers, e) => {
                if (onSelect) onSelect(day, selectedDay, activeModifiers, e)
                close()
              }}
            />
          )}
        </Popover.Panel>
      </Popover>
    </div>
  )
}
